import React, { useState, useEffect, useMemo } from "react";
import { FaChevronLeft } from "react-icons/fa6";
import ViewTable from "./ViewTable";
import Pagination from "../Layout/Pagination";
import SearchSection from "./SearchSection";

const ViewComponent = ({ handleBack, selectedData }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleHeaders, setVisibleHeaders] = useState([
    "dates",
    "distributor_name",
    "doctorsname",
    "product_code",
    "quantity",
    "revenue",
    "action",
  ]);

  // All available table headers
  const tableHeaders = [
    { label: "Date", value: "dates" },
    { label: "Distributor", value: "distributor_name" },
    { label: "Doctor", value: "doctorsname"},
    { label: "Product Name", value: "product_code"},
    { label: "Quantity", value: "quantity"},
    { label: "name", value: "username" },
    { label: "Patient Name", value: "patientname" },
    { label: "Tracking Status", value: "tracking_status" },
    { label: "Revenue", value: "revenue" },
    { label: "Action", value: "action" },
  ];

  // Filter data based on search query
  const filteredData = useMemo(() => {
    if (!selectedData || selectedData.length === 0) return [];
    
    if (!searchQuery.trim()) return selectedData;
    
    return selectedData.filter(item => {
      return visibleHeaders.some(header => {
        const value = item[header];
        if (value === null || value === undefined) return false;
        return value.toString().toLowerCase().includes(searchQuery.toLowerCase());
      });
    });
  }, [selectedData, searchQuery, visibleHeaders]);

  // Calculate pagination values
  const totalCount = filteredData.length;
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  
  // Get current page data
  const currentPageData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, itemsPerPage]);

  // Reset to first page when search changes or items per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, itemsPerPage]);

  // Reset to first page if current page exceeds total pages
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const renderTableCell = (item, header) => {
    switch (header) {
      case "revenue":
        const price = parseFloat(item.unitprice || 0);
        const quantity = parseInt(item.quantity || 0, 10);
        return `₹${(price * quantity).toLocaleString()}`;
      case "totalSales":
        return item.totalSales?.toLocaleString() || 0;
      case "dates":
        // Format date if needed
        return item[header] ? new Date(item[header]).toLocaleDateString() : "N/A";
      default:
        return item[header] || "N/A";
    }
  };

  console.log("selectedData:", selectedData);
  console.log("filteredData:", filteredData);
  console.log("currentPageData:", currentPageData);
  console.log("totalCount:", totalCount, "totalPages:", totalPages);

  return (
    <div className="px-0 sm:px-4 pt-4">
      <div className="flex mb-10">
        <button onClick={handleBack}>
          <FaChevronLeft className="w-5 h-5 text-text1" />
        </button>
        <h1 className="text-3xl leading-9 font-bold text-text2 ml-2">
          Data View ({totalCount} records)
        </h1>
      </div>
      
      <SearchSection
        onSearch={handleSearch}
        tablefields={tableHeaders}
        selectedHeaders={visibleHeaders}
        onHeadersChange={setVisibleHeaders}
      />
      
      <ViewTable
        data={currentPageData}
        headers={tableHeaders.filter((header) =>
          visibleHeaders.includes(header.value)
        )}
        renderCell={renderTableCell}
      />
      
      {totalCount > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          onPageChange={handlePageChange}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      )}
      
      {totalCount === 0 && (
        <div className="text-center py-8 text-gray-500">
          No data found
          {searchQuery && (
            <div className="mt-2">
              <button 
                onClick={() => setSearchQuery("")}
                className="text-blue-500 hover:underline"
              >
                Clear search
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ViewComponent;